import { useCallback, useEffect, useRef, useState } from 'react';
import { markPaywallSeen } from '@/lib/analytics/track';
import { supabase } from '@/lib/supabase';
import { isIOSNative, isAndroidNative } from '@/lib/platform';
import { PaywallEmailCTA } from '@/components/paywall-email-cta';
import { PurchaseButton } from '@/components/purchase-button';
import { pollExerciseUnlock } from '@/lib/exercise-v2/unlock-poll';
import { rememberExerciseExit } from '@/lib/exercise-exit';
import { markCheckoutStarted, passCheckoutMark } from '@/lib/analytics/track';
import {
  PassDurationGrid,
  passCheckoutLabel,
  passPitch,
  usePassChoice,
} from '@/components/pass-duration-grid';

/**
 * Carte paywall affichée dans le player quand l'élève atteint la fin de la zone
 * gratuite sans accès. Réplique le bloc tarifaire V1 : sélection de durée
 * (24h / 7j / 30j), checkout Stripe (`create-pass-checkout`) et lien parent
 * (`create-parent-link`). Tarif épreuves vs normal selon `chapterType`.
 *
 * Avant le checkout, l'attempt en cours est mémorisé en sessionStorage pour que
 * le retour de Stripe (`?pass_success=1`) reprenne l'exercice là où il était.
 *
 * ⚠️ Sur natif (`isNativeApp()`), Guideline App Store 3.1.1 + Google Play
 * Reader Apps policy interdisent tout CTA d'achat ou lien Stripe. On rend
 * alors une carte « Reader app » avec un bouton d'opt-in explicite
 * `PaywallEmailCTA` qui déclenche l'envoi d'un email (autorisé par Guideline
 * 3.1.3, update janvier 2024). Web inchangé.
 */

export type PassDuration = '24h' | '7d' | '30d';
type ChapterType = 'normal' | 'epreuves';

export const PASS_PRICES: Record<ChapterType, Record<PassDuration, string>> = {
  normal: { '24h': '1,99 €', '7d': '3,99 €', '30d': '9,99 €' },
  epreuves: { '24h': '2,99 €', '7d': '5,99 €', '30d': '15,99 €' },
};

export const DURATION_LABELS: Record<PassDuration, string> = {
  '24h': '24 heures',
  '7d': '7 jours',
  '30d': '30 jours',
};

export const DURATION_SUBLABELS: Record<PassDuration, string> = {
  '24h': 'Accès unique',
  '7d': 'Abonnement · résiliable',
  '30d': 'Abonnement · résiliable',
};

export function ExercisePaywallCard({
  chapterType,
  exerciseId,
  attemptId,
  onUnlocked,
}: {
  chapterType: ChapterType;
  exerciseId: string;
  attemptId: string | null;
  /**
   * Appelé quand l'accès a été débloqué APRÈS un achat natif (IAP) : le player
   * réinvalide sa requête et retire le paywall pour reprendre l'exercice.
   * Non utilisé sur web (le retour Stripe recharge la page → `?pass_success=1`).
   */
  onUnlocked?: () => void;
}) {
  // Tunnel de conversion « contenu bloqué → achat » (0718).
  useEffect(
    () => markPaywallSeen(chapterType === 'epreuves' ? 'epreuve' : 'exercice'),
    [chapterType],
  );
  const {
    settings: offerSettings,
    duration: selectedDuration,
    setDuration: setSelectedDuration,
  } = usePassChoice('7d');
  // ── Activation post-achat natif ──────────────────────────────────────────
  // `activating` : l'achat IAP a réussi, on sonde l'accès (webhook RC async).
  // `activationTimedOut` : le webhook n'a pas basculé l'accès dans le délai.
  const [activating, setActivating] = useState(false);
  const [activationTimedOut, setActivationTimedOut] = useState(false);
  const cancelledRef = useRef(false);
  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  const runUnlockPolling = useCallback(async () => {
    setActivating(true);
    setActivationTimedOut(false);
    const unlocked = await pollExerciseUnlock({
      isCancelled: () => cancelledRef.current,
      checkAllowed: async () => {
        const { data } = await (
          supabase.rpc as unknown as (
            fn: string,
            args: Record<string, unknown>,
          ) => Promise<{ data: { access?: { allowed?: boolean } } | null }>
        )('get_exercise_v2_for_student', { p_exercise_id: exerciseId });
        return !!data?.access?.allowed;
      },
    });
    if (cancelledRef.current) return;
    if (unlocked) onUnlocked?.();
    else setActivationTimedOut(true);
  }, [exerciseId, onUnlocked]);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [isParentLinkLoading, setIsParentLinkLoading] = useState(false);
  const [parentLinkUrl, setParentLinkUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const id = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(id);
  }, [copied]);

  async function handleCheckout() {
    setIsCheckoutLoading(true);
    if (attemptId) {
      sessionStorage.setItem(`exercise-attempt-${exerciseId}`, attemptId);
    }
    rememberExerciseExit(exerciseId, null);
    markCheckoutStarted(passCheckoutMark(chapterType, selectedDuration));
    const { data, error } = await supabase.functions.invoke('create-pass-checkout', {
      body: { type: chapterType, duration: selectedDuration, return_to: window.location.href },
    });
    if (error || !data?.url) {
      sessionStorage.removeItem(`exercise-attempt-${exerciseId}`);
      setIsCheckoutLoading(false);
      return;
    }
    window.location.href = data.url as string;
  }

  async function handleParentLink() {
    setIsParentLinkLoading(true);
    const { data, error } = await supabase.functions.invoke('create-parent-link', {
      body: { offer_type: chapterType, duration: selectedDuration },
    });
    setIsParentLinkLoading(false);
    if (error) return;
    setParentLinkUrl((data as { url: string }).url);
  }

  async function handleCopy() {
    if (!parentLinkUrl) return;
    await navigator.clipboard.writeText(parentLinkUrl);
    setCopied(true);
  }

  async function handleShare() {
    if (!parentLinkUrl || !navigator.share) return;
    await navigator.share({
      title: 'Paiement Jeraisonne',
      text: 'Clique ici pour payer mon accès Jeraisonne',
      url: parentLinkUrl,
    });
  }

  const isEpreuves = chapterType === 'epreuves';

  // ── iOS natif → IAP RevenueCat (App Store Guideline 3.1.1) ───────────────
  // Depuis 1.0.9, Apple exige un In-App Purchase pour tout contenu payé
  // consommé dans l'app. On propose l'abonnement mensuel via `PurchaseButton`
  // qui ouvre la feuille système Apple. Le webhook RC → subscriptions.
  if (isIOSNative()) {
    // Achat effectué → on attend l'activation de l'accès, puis on reprend l'exo.
    if (activating) {
      return (
        <div className="border-primary-200 from-primary-50 to-ink-50 space-y-4 rounded-2xl border-2 bg-gradient-to-b p-5 text-center">
          <p className="text-4xl">✅</p>
          <h3 className="font-display text-ink-900 text-xl font-extrabold">
            Merci pour ton soutien !
          </h3>
          {activationTimedOut ? (
            <>
              <p className="text-ink-500 font-sans text-sm leading-relaxed">
                Ton accès s'active dans quelques instants. Réessaie pour reprendre l'exercice.
              </p>
              <button
                onClick={runUnlockPolling}
                className="bg-primary-400 font-display hover:bg-primary-500 min-h-11 w-full rounded-xl py-3.5 text-sm font-bold text-white shadow transition-all active:scale-[.98]"
              >
                Reprendre l'exercice
              </button>
            </>
          ) : (
            <>
              <p className="text-ink-500 font-sans text-sm leading-relaxed">
                On débloque la suite de ton exercice…
              </p>
              <div
                className="border-primary-400 mx-auto h-6 w-6 animate-spin rounded-full border-2 border-t-transparent"
                aria-label="Activation en cours"
              />
            </>
          )}
        </div>
      );
    }

    return (
      <div className="border-primary-200 from-primary-50 to-ink-50 space-y-4 rounded-2xl border-2 bg-gradient-to-b p-5">
        <div className="space-y-1 text-center">
          <p className="text-primary-500 font-sans text-[11px] font-semibold uppercase tracking-wider">
            Tu étais lancé… 🚀
          </p>
          <h3 className="font-display text-ink-900 text-xl font-extrabold">Débloque la suite</h3>
          <p className="text-ink-500 font-sans text-sm leading-relaxed">
            {isEpreuves
              ? 'Continue avec un accès complet à Jeraisonne — exercices et épreuves officielles.'
              : 'Continue avec un accès complet à Jeraisonne.'}
          </p>
        </div>

        <PurchaseButton
          productId="jeraisonne_student_monthly"
          onPurchased={runUnlockPolling}
          context={{
            source: 'exercise-paywall-card',
            chapter_type: chapterType,
            exercise_id: exerciseId,
            attempt_id: attemptId,
          }}
        />

        <p className="text-ink-400 text-center font-sans text-xs">
          Ton professeur peut aussi t'assigner cet exercice gratuitement
        </p>
      </div>
    );
  }

  // ── Android natif → Reader app (Google Play Reader Apps policy) ───────────
  // Android reste en Reader app pour l'instant : Google est moins strict,
  // on n'a pas encore créé le product Play Console. À migrer vers
  // `PurchaseButton` quand Google Play Billing sera configuré côté RC.
  if (isAndroidNative()) {
    return (
      <div className="border-primary-200 from-primary-50 to-ink-50 space-y-4 rounded-2xl border-2 bg-gradient-to-b p-5">
        <div className="space-y-1 text-center">
          <p className="text-primary-500 font-sans text-[11px] font-semibold uppercase tracking-wider">
            Tu étais lancé… 🚀
          </p>
          <h3 className="font-display text-ink-900 text-xl font-extrabold">Débloque la suite</h3>
          <p className="text-ink-500 font-sans text-sm leading-relaxed">
            {isEpreuves
              ? "Continue avec l'accès aux épreuves officielles (Brevet, Bac…). On peut t'envoyer les formules par email."
              : "Continue avec un accès complet à Jeraisonne. On peut t'envoyer les formules par email."}
          </p>
        </div>

        <PaywallEmailCTA
          context={{
            source: 'exercise-paywall-card',
            chapter_type: chapterType,
            exercise_id: exerciseId,
            attempt_id: attemptId,
          }}
        />

        <p className="text-ink-400 text-center font-sans text-xs">
          Ton professeur peut aussi t'assigner cet exercice gratuitement
        </p>
      </div>
    );
  }

  return (
    <div className="border-primary-200 from-primary-50 to-ink-50 space-y-4 rounded-2xl border-2 bg-gradient-to-b p-5">
      <div className="space-y-1 text-center">
        <p className="text-primary-500 font-sans text-[11px] font-semibold uppercase tracking-wider">
          Tu étais lancé… 🚀
        </p>
        <h3 className="font-display text-ink-900 text-xl font-extrabold">Débloque la suite</h3>
        <p className="text-ink-500 font-sans text-sm leading-relaxed">
          {isEpreuves
            ? `Accès aux épreuves officielles (Brevet, Bac…). ${passPitch(offerSettings)}`
            : `La première question est gratuite. ${passPitch(offerSettings)}`}
        </p>
      </div>

      <PassDurationGrid
        prices={PASS_PRICES[chapterType]}
        value={selectedDuration}
        onChange={setSelectedDuration}
        settings={offerSettings}
      />

      <button
        onClick={handleCheckout}
        disabled={isCheckoutLoading}
        className="bg-primary-400 font-display hover:bg-primary-500 min-h-11 w-full rounded-xl py-3.5 text-sm font-bold text-white shadow transition-all active:scale-[.98] disabled:opacity-60"
      >
        {isCheckoutLoading
          ? 'Redirection…'
          : passCheckoutLabel(
              selectedDuration,
              PASS_PRICES[chapterType][selectedDuration],
              offerSettings,
            )}
      </button>

      <div className="flex items-center gap-2">
        <div className="border-ink-200 flex-1 border-t" />
        <span className="text-ink-400 font-sans text-xs">ou</span>
        <div className="border-ink-200 flex-1 border-t" />
      </div>

      <button
        onClick={handleParentLink}
        disabled={isParentLinkLoading || !!parentLinkUrl}
        className="text-primary-600 border-primary-200 min-h-11 w-full rounded-xl border py-3 font-sans text-sm font-semibold transition-all active:scale-[.98] disabled:opacity-60"
      >
        {isParentLinkLoading ? 'Génération…' : 'Demander à mes parents'}
      </button>

      {parentLinkUrl && (
        <div className="bg-ink-50 border-ink-200 space-y-3 rounded-xl border p-4">
          <p className="text-ink-700 font-sans text-xs font-semibold">
            Partage ce lien à tes parents — ils peuvent payer sans créer de compte :
          </p>
          <div className="border-ink-200 rounded-lg border bg-white px-3 py-2">
            <p className="text-ink-600 break-all font-mono text-[11px]">{parentLinkUrl}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleCopy}
              className="border-ink-200 flex-1 rounded-lg border py-2 font-sans text-xs font-semibold transition-all active:scale-[.98]"
            >
              {copied ? 'Copié !' : 'Copier le lien'}
            </button>
            {typeof navigator.share === 'function' && (
              <button
                onClick={handleShare}
                className="bg-primary-400 flex-1 rounded-lg py-2 font-sans text-xs font-semibold text-white transition-all active:scale-[.98]"
              >
                Partager
              </button>
            )}
          </div>
        </div>
      )}

      <p className="text-ink-400 text-center font-sans text-xs">
        Ton professeur peut aussi t'assigner cet exercice gratuitement
      </p>
    </div>
  );
}
